import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useCallback, useMemo } from 'react';
import { FLEXBOX_GAP, THUMBNAIL_SIZE } from './constants';
const DrawerProviderContext = createContext(undefined);
const { Provider } = DrawerProviderContext;
/**
 * Provider context for the DrawerProvider
 */
export const useDrawerData = () => {
    const result = useContext(DrawerProviderContext);
    if (!result) {
        throw new Error('Should be used inside an DrawerProvider.');
    }
    return result;
};
/**
 * Provider whose context provides the ARIA labels for the ui elements as well as if an asset is used on the design.
 */
export const DrawerProvider = (props) => {
    const { children, usedAssets, onImageLoadStart, onImageLoadCompleted, onImageLoadError, disableDeleteButton, deleteOption, render, styleOptions, multiboxListAutoscrollOptions, pageCountOptions, } = props;
    const isAssetUsed = useCallback((asset, pageNumber) => {
        if (!usedAssets) {
            return false;
        }
        // Just in case the client puts more than one of the same id in the array, let's find them all
        const onlyIds = usedAssets.filter((data) => { var _a; return data.assetId === ((_a = asset.data) === null || _a === void 0 ? void 0 : _a.id); });
        if (onlyIds.length === 0) {
            return false;
        }
        // Loop through those and see if any of the pages are included in any of the arrays.
        const assetData = onlyIds.find((data) => data.pages.includes(pageNumber));
        if (!assetData) {
            return false;
        }
        return true;
    }, [usedAssets]);
    const styleOptionsMemo = useMemo(() => {
        if (!styleOptions) {
            return {
                thumbnailSize: THUMBNAIL_SIZE,
                flexboxGap: FLEXBOX_GAP,
            };
        }
        return styleOptions;
    }, [styleOptions]);
    const multiboxListAutoscrollOptionsMemo = useMemo(() => {
        var _a;
        return {
            isEnable: (_a = multiboxListAutoscrollOptions === null || multiboxListAutoscrollOptions === void 0 ? void 0 : multiboxListAutoscrollOptions.isEnable) !== null && _a !== void 0 ? _a : false,
            block: (multiboxListAutoscrollOptions === null || multiboxListAutoscrollOptions === void 0 ? void 0 : multiboxListAutoscrollOptions.block) || 'nearest',
        };
    }, [multiboxListAutoscrollOptions]);
    const pageCountOptionsMemo = useMemo(() => {
        var _a;
        return {
            isEnable: (_a = pageCountOptions === null || pageCountOptions === void 0 ? void 0 : pageCountOptions.isEnable) !== null && _a !== void 0 ? _a : false,
            customStyle: (pageCountOptions === null || pageCountOptions === void 0 ? void 0 : pageCountOptions.customStyle) || '',
        };
    }, [pageCountOptions]);
    const value = useMemo(() => ({
        isAssetUsed,
        onImageLoadStart,
        onImageLoadCompleted,
        onImageLoadError,
        disableDeleteButton,
        deleteOption,
        render,
        styleOptions: styleOptionsMemo,
        multiboxListAutoscrollOptions: multiboxListAutoscrollOptionsMemo,
        pageCountOptions: pageCountOptionsMemo,
    }), [
        isAssetUsed,
        onImageLoadStart,
        onImageLoadCompleted,
        onImageLoadError,
        disableDeleteButton,
        deleteOption,
        render,
        styleOptionsMemo,
        multiboxListAutoscrollOptionsMemo,
        pageCountOptionsMemo,
    ]);
    return _jsx(Provider, Object.assign({ value: value }, { children: children }));
};
DrawerProvider.displayName = 'DrawerProvider';
//# sourceMappingURL=DrawerProvider.js.map